import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Sightseeing/SightseeingTopic.css';
import './WeatherDisplay.css';
import { personas } from './Sightseeing/SightseeingLayout';
import { IsHtml } from './Utils/Utility';
import { CommentSection } from './Utils/CommentSectionUtility';
import { FetchCountryIso, ParseCoordinates } from './Utils/CountryUtility';
import { LocalClock } from './Utils/TimezoneUtility';
import { SightseeingLeafletContainer } from './LeafletMap/LeafletMapLayout';
import { useParams } from 'react-router-dom';
import { enableScroll } from './Scroll';
import { Helmet } from 'react-helmet';
import 'leaflet/dist/leaflet.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { FetchSightseeing, SightseeingPost } from './Sightseeing/SightseeingRequests';
import { WeatherDisplay, FetchWeatherForSightseeing, WeatherData } from './WeatherDisplay';
import { MultiSlider } from './MultiSlider';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface SightseeingTopicLocationState {
  selectedItemCursor?: string;
  selectedItemCursorToken?: string;
  selectedItemId?: number;
  category?: string;
  region?: string;
}

type SightseeingTopicPost = SightseeingPost & {
  comment_section?: number;
  [key: string]: unknown;
};

const HelmetComponent = Helmet as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
const MultiSliderComponent = MultiSlider as React.ComponentType<any>;

const SightseeingTopic = () => {
  const navigate = useNavigate();
  const [sightseeingData, setSightseeingData] = useState<SightseeingTopicPost[]>([]); // State to store search results from the database
  const { country } = useParams<{ country: string }>(); // Extract the country from the URL
  const arrayContainerRef = useRef<HTMLDivElement | null>(null); // Create a ref for the .array-container div
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]); // Ref for each item
  const [activePost, setActivePost] = useState(0); // Track the active post
  const [activeMarker, setActiveMarker] = useState(0); // State to store active marker index
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation() as ReturnType<typeof useLocation> & { state?: SightseeingTopicLocationState };
  const locationState = location.state;

  const [currentCursor, setCurrentCursor] = useState('');
  const [oldCursor, setOldCursor] = useState('');
  const [newCursor, setNewCursor] = useState('');
  const [cursorToken, setCursorToken] = useState('');

  const [itemId, setItemId] = useState<number | null>(null);
  const [category, setCategory] = useState('');

  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<typeof personas>(personas);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [mainIso, setMainIso] = useState('');
  const [region, setRegion] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const setSightseeingDataTyped = (data: SightseeingPost[]) => {
    setSightseeingData(data as SightseeingTopicPost[]);
  };

  const handleItemClick = (post: SightseeingTopicPost, index: number, scrollToDescription = false) => {
    const [lat, lon] = ParseCoordinates(post.coordinates);
    localStorage.setItem('travel_itemId', String(post.id));
    setActivePost(index);
    if (!isNaN(lat) && !isNaN(lon)) {
      setMapCenter([lat, lon]);
      setActiveMarker(index);

      // Scroll the clicked item into view
      itemRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      if (scrollToDescription) {
        descriptionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      FetchWeatherForSightseeing(post.id, setWeatherData, setWeatherLoading);
    }
  };

  const openLightbox = (image?: string) => {
    if (!image) {
      return;
    }
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setSelectedImage('');
    setLightboxOpen(false);
  };

  const applyRegionFilter = (selectedRegion: string) => {
    if (!country) {
      return;
    }
    FetchCountryIso(country).then((iso) => {
      if (!iso) {
        return;
      }
      localStorage.removeItem('travel_cursor');
      localStorage.removeItem('travel_cursor_token');
      localStorage.removeItem('travel_itemId');
      localStorage.setItem('travel_region', selectedRegion);
      FetchSightseeing(
        iso,
        { cursor: '', cursorToken: '', category, region: selectedRegion, selectedItemId: undefined },
        setOldCursor,
        setNewCursor,
        setCurrentCursor,
        setCursorToken,
        setCategory,
        setRegion,
        setCurrentPage,
        setSightseeingDataTyped,
        setMapCenter,
        setActivePost,
        setActiveMarker,
        itemRefs,
      );
      setItemId(sightseeingData[activePost]?.id ?? null);
    });
  };

  useEffect(() => {
    if (!country) {
      return;
    }
    FetchCountryIso(country).then(async (iso) => {
      if (!iso) {
        return;
      }
      const [categoryRes, regionRes] = await Promise.all([
        axios.get(`${backendUrl}/worlddata/sightseeing/get-sightseeing-category-counts/${iso}/`),
        axios.get(
          `${backendUrl}/worlddata/sightseeing/get-sightseeing-region-counts/${iso}/${category ? `?travel_category=${encodeURIComponent(category)}` : ''}`,
        ),
      ]);

      const counts = categoryRes.data as Array<{ travel_category: string; count: number }>;
      const valid = personas.filter((p) => {
        const match = counts.find((c) => c.travel_category === p.id);
        return match && match.count > 0;
      });
      setAvailableCategories(valid);

      const regions = (regionRes.data || []) as Array<{ region: string }>;
      setAvailableRegions(regions.map((r) => r.region));
    });
  }, [country, category]);

  useEffect(() => {
    if (Array.isArray(sightseeingData) && sightseeingData.length > 0 && sightseeingData[activePost]) {
      const activeItem = sightseeingData[activePost];
      FetchWeatherForSightseeing(activeItem.id, setWeatherData, setWeatherLoading);
    }
  }, [activePost, sightseeingData]);

  useEffect(() => {
    if (!country) return;

    FetchCountryIso(country)
      .then((iso) => {
        if (!iso) {
          return;
        }
        setMainIso(iso);

        const savedItemId = localStorage.getItem('travel_itemId');
        const savedCategory = localStorage.getItem('travel_category');
        const savedRegion = localStorage.getItem('travel_region');
        const hasIncomingCursor = Boolean(locationState?.selectedItemCursor);
        const requestedStartCursor = hasIncomingCursor ? (locationState?.selectedItemCursor ?? '') : '';
        const startCursorToken = hasIncomingCursor ? (locationState?.selectedItemCursorToken ?? '') : '';
        const startCursor = requestedStartCursor && !startCursorToken ? '' : requestedStartCursor;
        if (!hasIncomingCursor) {
          localStorage.removeItem('travel_cursor');
          localStorage.removeItem('travel_cursor_token');
        }
        if (requestedStartCursor && !startCursorToken) {
          localStorage.removeItem('travel_cursor');
        }
        const startCategory = locationState?.category ?? savedCategory ?? '';
        const startRegion = locationState?.region ?? savedRegion ?? '';
        const startItemId = hasIncomingCursor ? (locationState?.selectedItemId ?? (savedItemId ? Number(savedItemId) : null) ?? null) : null;

        // Initialize history only if jumping
        setCurrentCursor(startCursor);
        setCursorToken(startCursorToken);
        setCategory(startCategory);
        setRegion(startRegion);
        setItemId(startItemId);

        FetchSightseeing(
          iso,
          {
            cursor: startCursor,
            cursorToken: startCursorToken,
            category: startCategory,
            region: startRegion,
            selectedItemId: startItemId, // only used on first load
          },
          setOldCursor,
          setNewCursor,
          setCurrentCursor,
          setCursorToken,
          setCategory,
          setRegion,
          setCurrentPage,
          setSightseeingDataTyped,
          setMapCenter,
          setActivePost,
          setActiveMarker,
          itemRefs,
        );
      })
      .catch((error) => {
        console.error('Error fetching country iso:', error);
      });
  }, [country, locationState]);

  useEffect(() => {
    const sv1 = localStorage.getItem('travel_cursor');
    if (sv1 !== null && sv1 !== '') {
      setCurrentCursor(sv1);
    }

    const sv2 = localStorage.getItem('travel_category');
    if (sv2 !== null && sv2 !== '') {
      setCategory(sv2);
    }

    const sv3 = localStorage.getItem('travel_itemId');
    if (sv3 !== null) {
      const parsedItemId = Number(sv3);
      if (Number.isFinite(parsedItemId)) {
        setItemId(parsedItemId);
      }
    }

    const sv4 = localStorage.getItem('travel_region');
    if (sv4 !== null && sv4 !== '') {
      setRegion(sv4);
    }

    const sv5 = localStorage.getItem('travel_cursor_token');
    if (sv5 !== null && sv5 !== '') {
      setCursorToken(sv5);
    }

    const arrayContainer = arrayContainerRef.current;

    // Call enableScroll and get the cleanup function
    const cleanupScroll = enableScroll(arrayContainer);

    // Cleanup scroll event listeners on component unmount
    return cleanupScroll;
  }, []); // Only runs once after the initial render

  useEffect(() => {
    if (currentCursor) {
      localStorage.setItem('travel_cursor', currentCursor);
    } else {
      localStorage.removeItem('travel_cursor');
    }
  }, [currentCursor]);

  useEffect(() => {
    if (cursorToken) {
      localStorage.setItem('travel_cursor_token', cursorToken);
    } else {
      localStorage.removeItem('travel_cursor_token');
    }
  }, [cursorToken]);

  useEffect(() => {
    if (itemId) {
      localStorage.setItem('travel_itemId', String(itemId));
    }
  }, [itemId]);

  useEffect(() => {
    if (category !== undefined && category !== null) {
      localStorage.setItem('travel_category', category);
    }
  }, [category]);

  useEffect(() => {
    if (region !== undefined && region !== null) {
      localStorage.setItem('travel_region', region);
    }
  }, [region]);

  useEffect(() => {
    if (locationState) {
      window.history.replaceState({}, document.title);
    }
  }, [locationState]);

  const selectedCategoryLabel = category ? availableCategories.find((c) => c.id === category)?.label || category : 'ALL';

  const encodedCountry = encodeURIComponent(country || '');
  const canonicalUrl = `https://livingoutofabackpack.com/traveltopics/${encodedCountry}/topics/sightseeing`;
  const socialImage = sightseeingData?.[activePost]?.images?.[0]
    ? `${backendUrl}/django-dynamic/pictures/${sightseeingData[activePost].images[0]}`
    : 'https://livingoutofabackpack.com/static/logo.jpg';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://livingoutofabackpack.com/home/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Travel Topics',
        item: 'https://livingoutofabackpack.com/traveltopics/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${country} Sightseeing`,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div className="SightseeingPage">
      <HelmetComponent>
        <title>Top Sightseeing in {country} | Attractions & Activities</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content={`Find top sightseeing places, attractions, and activities to enjoy in ${country}.`} />
        <meta
          name="keywords"
          content={`${country} travel spots, 
                sightseeing in ${country},
                top attractions in ${country},
                best places to visit in ${country}, 
                travel guide ${country}, 
                sightseeing tours ${country}, 
                famous landmarks ${country}, 
                vacation spots ${country}, 
                things to do in ${country}`}
        />
        <meta property="og:title" content={`Top Sightseeing in ${country} | Attractions & Activities`} />
        <meta property="og:description" content={`Explore the best places to visit, landmarks, and activities in ${country}.`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:alt" content={`Sightseeing highlights from ${country}`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </HelmetComponent>

      <h1>
        Travel spots for {country}
        <span className="iso-badge">
          {mainIso && (
            <img
              src={`https://flagcdn.com/w320/${mainIso.toLowerCase()}.png`}
              alt={`${mainIso} flag`}
              style={{
                width: '100%',
                maxWidth: '20px',
                height: 'auto',
                borderRadius: '4px',
              }}
            />
          )}
        </span>
      </h1>
      <h2>Experience all that {country} has to offer.</h2>
      <div className="array-container" ref={arrayContainerRef}>
        {Array.isArray(sightseeingData) && sightseeingData.length > 0 ? (
          sightseeingData.map((post, index) => (
            <div key={post.id} className={`array-item ${activePost === index ? 'active' : ''}`} onClick={() => handleItemClick(post, index)} ref={(el) => { itemRefs.current[index] = el; }}>
              <div className="picture-content">
                <div className="image-container">
                  <img src={`${backendUrl}/django-dynamic/pictures/${post.images.at(0)}`} title={post.place} alt={post.place} onClick={() => openLightbox(post.images.at(0))} />

                  {post.attributions?.[0] && (
                    <div className="attribution">
                      <div className="attribution-content">
                        {IsHtml(post.attributions[0]) ? <div dangerouslySetInnerHTML={{ __html: post.attributions[0] }} /> : <span>{post.attributions[0]}</span>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="block-title">
                {post.place} {post.activity}
              </div>

              <div className="PersonaIcons">
                {personas.map((d) =>
                  post.travel_category === d.id ? (
                    <span key={d.id} className="PersonaEmoji" title={d.explanation}>
                      {d.emoji}
                    </span>
                  ) : null,
                )}
              </div>

              <div className="quick-select">
                {post.coordinates && <div className="coordinates">{post.coordinates}</div>}

                <button
                  className="description-button"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the array-item click
                    handleItemClick(post, index, true); // Scroll to the description
                  }}
                >
                  Description
                </button>
              </div>
            </div>
          ))
        ) : (
          <p>travel spots and activities.</p>
        )}
      </div>

      <div className="filter-nav-row">
        <section className="filter-section" aria-label="Filter">
          <h3 className="filter-section-title">Filter</h3>
          <div className="filter-controls">
            <div className="category-filter-wrap">
              <label className="region-filter-label">Category Filter</label>
              <MultiSliderComponent
                type={'sightseeing'}
                options={availableCategories}
                onChange={(option: any) => {
                  if (!country) {
                    return;
                  }
                  FetchCountryIso(country).then((iso) => {
                    if (!iso) {
                      return;
                    }
                    const cat = option === 'ALL' ? '' : option.id;
                    localStorage.removeItem('travel_cursor');
                    localStorage.removeItem('travel_cursor_token');
                    localStorage.removeItem('travel_category');
                    localStorage.removeItem('travel_itemId');
                    localStorage.removeItem('travel_region');
                    FetchSightseeing(
                      iso,
                      { cursor: '', cursorToken: '', category: cat, region: '', selectedItemId: undefined },
                      setOldCursor,
                      setNewCursor,
                      setCurrentCursor,
                      setCursorToken,
                      setCategory,
                      setRegion,
                      setCurrentPage,
                      setSightseeingDataTyped,
                      setMapCenter,
                      setActivePost,
                      setActiveMarker,
                      itemRefs,
                    );
                    setItemId(sightseeingData[activePost]?.id ?? null);
                  });
                }}
              />
            </div>

            <div className="region-filter-wrap">
              <label htmlFor="region-filter" className="region-filter-label">
                Region Filter
              </label>
              <select
                id="region-filter"
                className="region-filter-select"
                value={region}
                onChange={(e) => applyRegionFilter(e.target.value)}
              >
                <option value="">ALL</option>
                {availableRegions.map((regionName) => (
                  <option key={regionName} value={regionName}>
                    {regionName}
                  </option>
                ))}
              </select>
              <div className="region-filter-hint">Regions shown for category: {selectedCategoryLabel}</div>
            </div>
          </div>
        </section>

        <div className="nav-controls">
          <div className="Pagination">
            <button
              className="PaginationButtonLeft"
              disabled={!newCursor}
              onClick={() => {
                if (!country) {
                  return;
                }
                FetchCountryIso(country).then((iso) => {
                  if (!iso) {
                    return;
                  }
                  FetchSightseeing(
                    iso,
                    { cursor: newCursor, cursorToken, category, region, selectedItemId: undefined },
                    setOldCursor,
                    setNewCursor,
                    setCurrentCursor,
                    setCursorToken,
                    setCategory,
                    setRegion,
                    setCurrentPage,
                    setSightseeingDataTyped,
                    setMapCenter,
                    setActivePost,
                    setActiveMarker,
                    itemRefs,
                  );
                });
              }}
            >
              <span className="material-icons">chevron_left</span>
            </button>

            <span className="PageIndicator">
              <div className="Title">Travel Posts</div>
              Page Number {currentPage}
            </span>

            <button
              className="PaginationButtonRight"
              disabled={!oldCursor}
              onClick={() => {
                if (!country) {
                  return;
                }
                FetchCountryIso(country).then((iso) => {
                  if (!iso) {
                    return;
                  }
                  FetchSightseeing(
                    iso,
                    { cursor: oldCursor, cursorToken, category, region, selectedItemId: undefined },
                    setOldCursor,
                    setNewCursor,
                    setCurrentCursor,
                    setCursorToken,
                    setCategory,
                    setRegion,
                    setCurrentPage,
                    setSightseeingDataTyped,
                    setMapCenter,
                    setActivePost,
                    setActiveMarker,
                    itemRefs,
                  );
                });
              }}
            >
              <span className="material-icons">chevron_right</span>
            </button>
          </div>

          {sightseeingData.length > 0 && sightseeingData[activePost] && (
            <button
              className="report-btn"
              onClick={(e) => {
                e.stopPropagation(); // prevent triggering the card click
                navigate(
                  `/reportpage?itemSearch=${encodeURIComponent(sightseeingData[activePost].place || sightseeingData[activePost].activity)}` +
                    `&category=sightseeing` +
                    `&id=${sightseeingData[activePost].id}` +
                    `&iso=${sightseeingData[activePost].iso}` +
                    `&country=${country}`,
                );
              }}
            >
              <span className="material-icons">flag</span>
              Report
            </button>
          )}
        </div>
      </div>

      <section className="content-section" aria-label="Content">
        <h3 className="content-section-title">Content</h3>
      <div className="PostWiki">
        <SightseeingLeafletContainer
          sightseeingData={sightseeingData}
          mapCenter={mapCenter}
          setActiveMarker={setActiveMarker}
          activeMarker={activeMarker}
          setActivePost={setActivePost}
          itemRefs={itemRefs}
        />
        {weatherLoading ? <div className="weather-loading">Loading weather...</div> : weatherData && <WeatherDisplay weather={weatherData} />}

        <div className="Description" ref={descriptionRef}>
          <div className="DescriptionTitleBanner">
            <h1>{sightseeingData.length > 0 && sightseeingData[activePost] ? sightseeingData[activePost].place || sightseeingData[activePost].activity : 'Place name.'}</h1>
            <h2>Description</h2>
          </div>
          <div className="DescriptionText">{sightseeingData.length > 0 && sightseeingData[activePost] ? sightseeingData[activePost].description : 'The travel spot description.'}</div>
        </div>

        {sightseeingData.length > 0 && sightseeingData[activePost] && sightseeingData[activePost].images
          ? sightseeingData[activePost].images.slice(1).map((image, index) => (
              <div className="ExtraPictures">
                <div className="picture-content">
                  <div className="image-container">
                    <img
                      key={index}
                      src={`${backendUrl}/django-dynamic/pictures/${image}`}
                      title={`${sightseeingData[activePost].place} - Extra Picture ${index + 1}`}
                      alt={`${sightseeingData[activePost].place} - Extra ${index + 1}`}
                      onClick={() => openLightbox(image)}
                    />

                    {sightseeingData[activePost].attributions?.[0] && (
                      <div className="attribution">
                        <div className="attribution-content">
                          {IsHtml(sightseeingData[activePost].attributions[0]) ? (
                            <div dangerouslySetInnerHTML={{ __html: sightseeingData[activePost].attributions[0] }} />
                          ) : (
                            <span>{sightseeingData[activePost].attributions[0]}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          : null}

        {sightseeingData.length > 0 && sightseeingData[activePost] && sightseeingData[activePost].coordinates && <LocalClock coordinates={sightseeingData[activePost].coordinates} />}
      </div>
      </section>

      {lightboxOpen && (
        <div className="lightbox" onClick={closeLightbox}>
          <img src={`${backendUrl}/django-dynamic/pictures/${selectedImage}`} alt="Enlarged view" />
          <button className="close-button" onClick={closeLightbox}>
            X
          </button>
        </div>
      )}

      {sightseeingData.length > 0 && sightseeingData[activePost] && typeof sightseeingData[activePost].comment_section === 'number' && (
        <CommentSection sectionId={sightseeingData[activePost].comment_section as number} />
      )}
    </div>
  );
};

export default SightseeingTopic;
